import type { UserInfo, NavigationItem, EmbeddedService } from '@/types';

const normalizeToken = (value: string) => value.trim().toLowerCase().replace(/_/g, '-');

const adminEntitlements = new Set(['admin', 'portal:admin']);

const defaultServicePermissions: Record<string, string[]> = {
  academy: ['portal:academy:view', 'academy:view'],
  community: ['portal:community:view', 'community:view'],
  launchpad: ['portal:launchpad:view', 'launchpad:view'],
  support: ['portal:support:view', 'support:view'],
  shop: ['portal:shop:view', 'shop:view'],
  portfolio: ['portal:portfolio:view', 'portfolio:view'],
  marketplace: ['portal:marketplace:view'],
  library: ['portal:library:view', 'portal:library:partner', 'portal:library:admin'],
  booking: ['portal:booking:view', 'portal:booking:action', 'portal:booking:admin'],
};

const getUserEntitlements = (user: UserInfo | null | undefined) =>
  new Set([...(user?.roles ?? []), ...(user?.permissions ?? [])].map(normalizeToken));

export const hasAnyPermission = (
  user: UserInfo | null | undefined,
  requiredPermissions?: string[]
) => {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  const entitlements = getUserEntitlements(user);
  if ([...entitlements].some((entitlement) => adminEntitlements.has(entitlement))) {
    return true;
  }

  return requiredPermissions.some((permission) => entitlements.has(normalizeToken(permission)));
};

export const filterAuthorizedNavigationItems = (
  items: NavigationItem[],
  user: UserInfo | null | undefined
): NavigationItem[] =>
  items
    .filter((item) => hasAnyPermission(user, item.permissions))
    .map((item) => ({
      ...item,
      children: item.children ? filterAuthorizedNavigationItems(item.children, user) : undefined,
    }));

export const filterAuthorizedServices = (
  services: EmbeddedService[],
  user: UserInfo | null | undefined
) =>
  services.filter((service) => {
    const requiredPermissions = service.permissions ?? defaultServicePermissions[service.id];
    return Boolean(requiredPermissions?.length) && hasAnyPermission(user, requiredPermissions);
  });
